import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
// KEY CHANGE 1: Import DefaultData to use theme colors and text
import DefaultData from "../CompanyData/DefaultData";

// KEY CHANGE 2: Added the features array for the left-side display, just like in LoginPage
const features = [
    { icon: 'ri-flight-takeoff-line', name: 'Flights' }, { icon: 'ri-building-line', name: 'Hotels' },
    { icon: 'ri-car-line', name: 'Cars' }, { icon: 'ri-bus-2-line', name: 'Bus' },
    { icon: 'ri-suitcase-line', name: 'Holidays' }, { icon: 'ri-visa-line', name: 'Visas' },
    { icon: 'ri-magic-line', name: 'AI Packages' }, { icon: 'ri-whatsapp-line', name: 'AI Chatbot' },
    { icon: 'ri-user-add-line', name: 'Leads' }, { icon: 'ri-global-line', name: 'Website' },
    { icon: 'ri-bar-chart-line', name: 'Analytics' }, { icon: 'ri-customer-service-2-line', name: 'Support' },
];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [values, setValues] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [btnSubmit, setBtnSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");

  // --- All your existing handler functions (handleInputChange, validate, etc.) remain unchanged ---
  // --- They are perfectly fine and handle the logic correctly. ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    let temp = {};
    temp.password = values.password !== "" && values.password.length >= 6;
    temp.confirmPassword = values.confirmPassword !== "" && values.confirmPassword.length > 0;
    temp.passwordsMatch = values.password === values.confirmPassword;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((prev) => !prev);

  const resetForm = () => {
    setValues({ password: "", confirmPassword: "" });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };
  
  const handleTryAgain = () => {
    setStatus(null);
    setMessage("");
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnSubmit(true);
      setLoading(true);
      const formData = {
        email: email,
        password: values.password,
        token: token.trim(),
        domainUrl: window.location.origin,
      };

      try {
        const response = await apiService.post(config.RESETPASSWORD, formData);
        setStatus(true);
        setMessage(response.data?.message || "Password changed successfully!");
        resetForm();
      } catch (error) {
        setStatus(false);
        const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
        setMessage(errorMessage);
      } finally {
        setBtnSubmit(false);
        setLoading(false);
      }
    }
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " is-invalid" : "";

  useEffect(() => {
    if (!searchParams) {
      navigate("/");
    } else {
      setEmail(searchParams.get("email") || "");
      var letparams = searchParams.toString();
      var splitparams = letparams.split("=");
      setToken(splitparams[2] || "");
    }
  }, [searchParams, navigate]);


  // KEY CHANGE 3: The entire JSX return is replaced with the new layout structure.
  return (
    <div style={{ backgroundColor: DefaultData.THEMECOLOR1, minHeight: '100vh' }}>
      <div className="container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="row align-items-center w-100">
          
          {/* Left Column: Branding and Features */}
          <div className="col-lg-7 text-center text-lg-start mb-5 mb-lg-0 d-none d-lg-block">
              <h1 className="display-6 fw-bolder mb-3" style={{ color: DefaultData.THEMECOLOR2 }}>{DefaultData.LANDINGPAGETITLE}</h1>
              <p className="fs-5 mb-5" style={{ color: DefaultData.THEMECOLOR2, opacity: 0.9 }}>{DefaultData.LANDINGPAGESUBTITLE}</p>
              <div className="row row-cols-6 justify-content-center justify-content-lg-start g-4">
                  {features.map((feature) => (
                      <div className="col" key={feature.name}>
                          <div className="text-center">
                              <div style={{ width: '70px', height: '70px', borderRadius: '50%', backgroundColor: DefaultData.THEMECOLOR3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: DefaultData.THEMECOLOR2, marginBottom: '0.5rem', margin: '0 auto' }}>
                                  <i className={`${feature.icon} fs-2`}></i>
                              </div>
                              <span className="fw-semibold" style={{ color: DefaultData.THEMECOLOR2 }}>{feature.name}</span>
                          </div>
                      </div>
                  ))}
              </div>
          </div>

          {/* Right Column: The Reset Password Card */}
          <div className="col-lg-5 d-flex justify-content-center justify-content-lg-end">
            <div className="card shadow-lg border-0" style={{ borderRadius: '1rem', width: '100%', maxWidth: '450px' }}>
              <div className="card-body p-lg-5 p-4">
                
                {/* Your existing conditional rendering logic now lives inside this card */}
                {status !== null ? (
                  status === true ? (
                    // Success State
                    <div className="text-center">
                      <div className="mb-4">
                        <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '4rem' }}></i>
                      </div>
                      <h4 className="mb-2">Password Reset!</h4>
                      <p className="text-muted mb-4">{message || "Your password has been successfully reset."}</p>
                      <Link to="/" className="btn w-100 fw-bold" style={{ backgroundColor: DefaultData.THEMECOLOR1, color: DefaultData.THEMECOLOR4 }}>
                        Return to Sign In
                      </Link>
                    </div>
                  ) : (
                    // Error State
                    <div className="text-center">
                      <div className="mb-4">
                          <i className="ri-error-warning-line text-danger" style={{ fontSize: '4rem' }}></i>
                      </div>
                      <h4 className="mb-2">Reset Failed</h4>
                      <p className="text-muted mb-4">{message || "Could not reset password. The link may be invalid or expired."}</p>
                      <button onClick={handleTryAgain} className="btn w-100 fw-bold" style={{ backgroundColor: DefaultData.THEMECOLOR1, color: DefaultData.THEMECOLOR4 }}>
                        Try Again
                      </button>
                    </div>
                  )
                ) : (
                  // Form State
                  <div>
                    <div className="text-center">
                      <img src="/assets/images/Zotrai.png" alt={`${DefaultData.DEFAULTBRANDNAME} Logo`} style={{ height: '35px' }} className="mb-3" />
                    </div>
                    <h4 className="text-center">Reset Password</h4>
                    <p className="text-muted text-center mb-4">Create your new password.</p>

                    <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="password-input">New Password</label>
                        <div className="position-relative">
                          <input
                            className={`form-control pe-5 ${applyErrorClass("password")}`}
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={values.password}
                            onChange={handleInputChange}
                            placeholder="Enter new password"
                            disabled={btnSubmit}
                          />
                           <button type="button" className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted" onClick={togglePasswordVisibility}>
                                <i className={`ri-eye-${showPassword ? 'line' : 'off-line'} fs-5`}></i>
                           </button>
                        </div>
                        {errors.password === false && (
                          <div className="invalid-feedback d-block">Password must be at least 6 characters long.</div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label" htmlFor="confirm-password-input">Confirm New Password</label>
                        <div className="position-relative">
                          <input
                            className={`form-control pe-5 ${applyErrorClass("confirmPassword") || applyErrorClass("passwordsMatch")}`}
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={values.confirmPassword}
                            onChange={handleInputChange}
                            placeholder="Confirm new password"
                            disabled={btnSubmit}
                          />
                          <button type="button" className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted" onClick={toggleConfirmPasswordVisibility}>
                              <i className={`ri-eye-${showConfirmPassword ? 'line' : 'off-line'} fs-5`}></i>
                          </button>
                        </div>
                        {errors.confirmPassword === false && (
                          <div className="invalid-feedback d-block">Please confirm your password.</div>
                        )}
                        {errors.passwordsMatch === false && (
                          <div className="invalid-feedback d-block">Passwords do not match.</div>
                        )}
                      </div>

                      <div className="mt-4">
                        <button type="submit" className="btn w-100 fw-bold" style={{ backgroundColor: DefaultData.THEMECOLOR1, color: DefaultData.THEMECOLOR4 }} disabled={btnSubmit}>
                          {btnSubmit ? "Resetting..." : "Reset Password"}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <p className="mb-0 text-muted">
                          Remember your password?{" "}
                          <Link to="/" className="fw-semibold text-decoration-underline" style={{ color: DefaultData.THEMECOLOR2 }}>
                            Back to Login
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}